import classNames from 'classnames';
import { Button, Input } from 'components/atoms';
import { useAudioPlayer } from 'hooks/useAudioPlayer';
import { usePlayerProvider } from 'providers/PlayerProvider';
import { useStoreState } from 'providers/StoreStateContext';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiPauseFill,
  PiPlayFill,
  PiSpeakerHigh,
  PiSpeakerX,
  PiX,
} from 'react-icons/pi';
import { Link } from 'react-router-dom';
import { useGetTrack, usePostTrackPlaybackTicket } from 'services/tracks';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import './TrackPlayer.scss';

const formatPlaybackTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00';
  }

  const totalSeconds = Math.floor(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const TrackPlayer = () => {
  const { t } = useTranslation();
  const { playerTrackId, setPlayerTrackId } = useStoreState();
  const { setIsTrackPlaying } = usePlayerProvider();
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);

  const track = useGetTrack(playerTrackId ?? '', !!playerTrackId);
  const trackPlaybackTicket = usePostTrackPlaybackTicket();

  const playerTrack = useMemo(
    () =>
      track.data
        ? {
            title: track.data.mainTitle.title,
            artists: track.data.artists,
            imageUrl: null as string | null,
          }
        : undefined,
    [track.data]
  );

  const artistsLabel = useMemo(
    () =>
      Array.from(
        new Set(
          track.data?.artists?.map(artist => artist.nickname).filter(Boolean) ??
            []
        )
      ).join(', '),
    [track.data?.artists]
  );

  const {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    trackVolume,
    handleLoadedMetadata,
    handleTimeUpdate,
    togglePlay,
    handleSeekStart,
    handleSeekEnd,
    handleVolumeChange,
    toggleMute,
    setCurrentTime,
    handleEnded,
    isBuffering,
    handleCanPlay,
    handlePlaying,
    handleWaiting,
    handleError,
  } = useAudioPlayer(playbackUrl, playerTrack);

  useEffect(() => {
    if (!playerTrackId) {
      setPlaybackUrl(null);
      setIsTrackPlaying(false);
      return;
    }

    let isCancelled = false;

    const getTicket = async () => {
      try {
        const ticket = await trackPlaybackTicket.mutateAsync(playerTrackId);
        if (!isCancelled) {
          setPlaybackUrl(ticket.playbackUrl);
        }
      } catch (error) {
        if (!isCancelled) {
          setPlaybackUrl(null);
          setIsTrackPlaying(false);
          try {
            if (error instanceof Error) {
              onErrorShowAllErrorsToast(error);
            }
          } catch {
            // Non-API errors (plain Error messages) are not JSON payloads.
          }
        }
      }
    };

    void getTicket();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when track id changes
  }, [playerTrackId]);

  const seekMax = duration || 100;
  const volumePercent = trackVolume * 100;
  const isLoading = trackPlaybackTicket.isPending || isBuffering;

  return (
    <div className={classNames('track-player', { visible: !!playerTrackId })}>
      <audio
        ref={audioRef}
        src={playbackUrl || undefined}
        preload='auto'
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onWaiting={handleWaiting}
        onPlaying={handlePlaying}
        onCanPlay={handleCanPlay}
        onError={handleError}
      />

      <div className='track-player-content'>
        <div className='track-player-data'>
          <Link
            className='track-player-data-title'
            to={`/track/${playerTrackId}/file`}
            title={track.data?.mainTitle.title}>
            {track.data?.mainTitle.title}
          </Link>
          {artistsLabel ? (
            <p className='track-player-data-artists' title={artistsLabel}>
              {artistsLabel}
            </p>
          ) : null}
        </div>

        <div className='track-player-slider'>
          <Button
            type='button'
            className='track-player-play'
            variant={BUTTON_VARIANT.submit}
            onlyIcon
            isLoading={isLoading}
            disabled={!playbackUrl && !isLoading}
            onClick={togglePlay}>
            <Button.Description
              description={isPlaying ? t('button.pause') : t('button.play')}
            />
            <Button.Icon icon={isPlaying ? <PiPauseFill /> : <PiPlayFill />} />
          </Button>

          <p className='track-player-slider-time'>
            {formatPlaybackTime(currentTime)}
          </p>

          <Input.Slider
            className='track-player-seek'
            min={0}
            max={seekMax}
            step='any'
            value={currentTime}
            onChange={event => setCurrentTime(Number(event.target.value))}
            onMouseDown={handleSeekStart}
            onMouseUp={event =>
              handleSeekEnd(Number((event.target as HTMLInputElement).value))
            }
            onTouchEnd={event =>
              handleSeekEnd(Number((event.target as HTMLInputElement).value))
            }
          />

          <p className='track-player-slider-time'>
            {formatPlaybackTime(duration)}
          </p>
        </div>

        <div className='track-player-sound'>
          <Button type='button' isClear onlyIcon onClick={toggleMute}>
            <Button.Description description={t('button.toggle-sound')} />
            <Button.Icon
              icon={trackVolume === 0 ? <PiSpeakerX /> : <PiSpeakerHigh />}
            />
          </Button>

          <Input.Slider
            className='track-player-volume'
            min={0}
            max={100}
            step={5}
            value={volumePercent}
            onChange={event =>
              handleVolumeChange(Number(event.target.value) / 100)
            }
          />
        </div>

        <Button
          type='button'
          className='track-player-close'
          isClear
          onlyIcon
          onClick={() => {
            setPlayerTrackId(null);
            setPlaybackUrl(null);
            setIsTrackPlaying(false);
          }}>
          <Button.Description description={t('popup.close')} />
          <Button.Icon icon={<PiX />} />
        </Button>
      </div>
    </div>
  );
};

export default TrackPlayer;
